import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Toast } from "primereact/toast";
import {
  FaMobileAlt,
  FaRocket,
  FaPlus,
  FaTrash,
  FaSyncAlt,
  FaTimes,
  FaExclamationTriangle,
  FaCheck,
  FaLink,
  FaCopy,
  FaExternalLinkAlt,
  FaLock,
  FaUnlock,
  FaBroadcastTower,
  FaHistory,
  FaInbox,
  FaInfoCircle,
  FaRedo,
  FaDownload,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes auFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes auFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes auSlide { from { transform: translateX(100%); } to { transform: none; } }
@keyframes auZoom { from { opacity: 0; transform: scale(.95); } to { opacity: 1; transform: none; } }
@keyframes auPulse { 0% { box-shadow: 0 0 0 0 rgba(16,185,129,.55); } 70% { box-shadow: 0 0 0 10px rgba(16,185,129,0); } 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); } }
@keyframes auFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
.au-in { animation: auFadeUp .5s cubic-bezier(.2,.7,.2,1) both; }
.au-fade { animation: auFade .2s ease both; }
.au-slide { animation: auSlide .3s cubic-bezier(.2,.8,.2,1) both; }
.au-zoom { animation: auZoom .25s cubic-bezier(.2,.8,.2,1) both; }
.au-live { animation: auPulse 1.8s ease-out infinite; }
.au-float { animation: auFloat 4s ease-in-out infinite; }
.au-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.au-press:hover { transform: translateY(-1px); }
.au-press:active { transform: scale(.96); }
@media (prefers-reduced-motion: reduce) {
  .au-in, .au-fade, .au-slide, .au-zoom, .au-live, .au-float { animation: none; }
  .au-press:hover, .au-press:active { transform: none; }
}
`;

const CARD = "bg-white dark:bg-secondary-dark-bg rounded-2xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500";

const EMPTY = { latest_version_code: "", latest_version_name: "", apk_url: "", force_update: 0 };

/* ================= helpers ================= */

const num = (v) => Number(v || 0);
const parseAny = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) => (d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const fmtTime = (d) => (d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "");
const ago = (d) => {
  if (!d) return "";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  const days = Math.floor(s / 86400);
  return `${days} day${days > 1 ? "s" : ""} ago`;
};
const isForce = (u) => String(u.force_update) === "1";
const fileName = (url) => {
  try {
    return decodeURIComponent(new URL(url).pathname.split("/").pop() || url);
  } catch (e) {
    return url;
  }
};
const linkKind = (url) => {
  const u = String(url || "").toLowerCase();
  if (!u) return null;
  if (u.includes("play.google.com")) return "Play Store";
  if (u.includes("drive.google.com")) return "Google Drive";
  if (/\.apk(\?|$)/.test(u)) return "APK file";
  return "Web link";
};

/* ================= page ================= */

const AppUpdates = () => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [editor, setEditor] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // {type:'delete'|'republish', item}
  const [working, setWorking] = useState(false);

  const toast = useRef(null);
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API (same actions) ----- */

  const post = useCallback(
    async (action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/app_update.php`, { method: "POST", body: fd, credentials: "include" });
      const text = await res.text();
      try {
        return JSON.parse(text);
      } catch (e) {
        throw new Error(res.status === 404 ? "app_update.php is not on the server" : "Server sent a wrong reply");
      }
    },
    [baseUrl]
  );

  const fetchUpdates = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await post("appUpdateList");
      if (res?.error === 0) setUpdates(Array.isArray(res.updates) ? res.updates : []);
      else throw new Error(res?.mysql_error || res?.error || "Could not load releases");
    } catch (err) {
      console.error(err);
      setLoadError(err.message);
    }
    setLoading(false);
  }, [post]);

  useEffect(() => {
    fetchUpdates();
  }, [fetchUpdates]);

  /* ----- derived ----- */

  // The app reads the row with the newest date (see verifyMpin in login.php)
  const live = useMemo(() => {
    if (!updates.length) return null;
    return [...updates].sort((a, b) => (parseAny(b.date_time)?.getTime() || 0) - (parseAny(a.date_time)?.getTime() || 0) || num(b.update_id) - num(a.update_id))[0];
  }, [updates]);

  const highest = useMemo(() => updates.reduce((m, u) => (num(u.latest_version_code) > num(m?.latest_version_code) ? u : m), null), [updates]);

  const timeline = useMemo(
    () => [...updates].sort((a, b) => (parseAny(b.date_time)?.getTime() || 0) - (parseAny(a.date_time)?.getTime() || 0) || num(b.update_id) - num(a.update_id)),
    [updates]
  );

  const stats = useMemo(
    () => ({
      total: updates.length,
      force: updates.filter(isForce).length,
    }),
    [updates]
  );

  const liveBehind = live && highest && num(highest.latest_version_code) > num(live.latest_version_code);

  /* ----- form ----- */

  const maxCode = highest ? num(highest.latest_version_code) : 0;
  const errors = useMemo(() => {
    const e = {};
    const code = String(form.latest_version_code).trim();
    if (!code) e.latest_version_code = "Version code is required";
    else if (!/^\d+$/.test(code)) e.latest_version_code = "Whole number only (from build.gradle versionCode)";
    else if (num(code) <= 0) e.latest_version_code = "Must be more than 0";
    if (!String(form.latest_version_name).trim()) e.latest_version_name = "Version name is required";
    const url = String(form.apk_url).trim();
    if (!url) e.apk_url = "Download link is required";
    else if (!/^https?:\/\/\S+$/i.test(url)) e.apk_url = "Paste a full link starting with https://";
    return e;
  }, [form]);
  const lowerThanLive = form.latest_version_code !== "" && maxCode > 0 && num(form.latest_version_code) <= maxCode;

  const openNew = () => {
    setForm({ ...EMPTY, latest_version_code: maxCode ? String(maxCode + 1) : "" });
    setTouched({});
    setEditor(true);
  };

  const save = async () => {
    setTouched({ latest_version_code: 1, latest_version_name: 1, apk_url: 1 });
    if (Object.keys(errors).length) {
      toast.current?.show({ severity: "error", summary: "Check the form", detail: Object.values(errors)[0] });
      return;
    }
    setSaving(true);
    try {
      const res = await post("addAppUpdate", {
        latest_version_code: String(form.latest_version_code).trim(),
        latest_version_name: String(form.latest_version_name).trim(),
        apk_url: String(form.apk_url).trim(),
        force_update: form.force_update ? 1 : 0,
      });
      if (res?.error === 0 || res?.status === true) {
        toast.current?.show({ severity: "success", summary: "Published", detail: `v${form.latest_version_name} is now live for all users` });
        setEditor(false);
        fetchUpdates();
      } else throw new Error(res?.message || res?.error || "Could not publish");
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Error", detail: e.message });
    }
    setSaving(false);
  };

  const runConfirm = async () => {
    if (!confirm) return;
    const { type, item } = confirm;
    setWorking(true);
    try {
      let res;
      if (type === "delete") res = await post("deleteAppUpdate", { update_id: item.update_id });
      else
        res = await post("addAppUpdate", {
          latest_version_code: item.latest_version_code,
          latest_version_name: item.latest_version_name,
          apk_url: item.apk_url,
          force_update: type === "toggleForce" ? (isForce(item) ? 0 : 1) : item.force_update,
        });
      if (res?.error !== 0 && res?.status !== true) throw new Error(res?.message || res?.error || "Failed");
      toast.current?.show({
        severity: "success",
        summary: "Done",
        detail: type === "delete" ? `v${item.latest_version_name} removed` : type === "toggleForce" ? `Force update ${isForce(item) ? "turned off" : "turned on"}` : `v${item.latest_version_name} is live again`,
      });
      setConfirm(null);
      fetchUpdates();
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Error", detail: e.message });
    }
    setWorking(false);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (confirm) {
        if (e.key === "Escape" && !working) setConfirm(null);
        return;
      }
      if (editor) {
        if (e.key === "Escape" && !saving) setEditor(false);
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const copyText = (t, label = "Copied") => {
    navigator.clipboard?.writeText(String(t));
    toast.current?.show({ severity: "info", summary: label, detail: String(t), life: 1500 });
  };

  /* ----- phone preview ----- */

  const Phone = ({ name, force, empty }) => (
    <div className="au-float relative mx-auto w-[210px] h-[400px] rounded-[2.2rem] bg-slate-900 p-2.5 shadow-2xl">
      <div className="absolute top-3 left-1/2 -translate-x-1/2 w-16 h-4 rounded-full bg-slate-800" />
      <div className="w-full h-full rounded-[1.7rem] bg-gradient-to-b from-slate-100 to-slate-300 overflow-hidden relative">
        <div className="absolute inset-0 bg-slate-900/40" />
        <div className="absolute inset-x-3 bottom-6 rounded-2xl bg-white p-4 text-center shadow-xl">
          {empty ? (
            <>
              <p className="text-sm font-bold text-slate-900">No update prompt</p>
              <p className="text-[11px] text-slate-500 mt-1">Users are not asked to update until you publish a release.</p>
            </>
          ) : (
            <>
              <span className="mx-auto w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center">
                <FaRocket />
              </span>
              <p className="mt-2 text-sm font-bold text-slate-900">Update available</p>
              <p className="text-[11px] text-slate-500">Version {name || "?"} is ready</p>
              <div className="mt-3 h-8 rounded-lg bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">Update now</div>
              {force ? <p className="mt-2 text-[10px] font-semibold text-rose-600">Must update to keep using the app</p> : <p className="mt-2 text-[11px] text-slate-500">Later</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );

  /* ================= render ================= */

  return (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="au-in flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">App Management</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">App Updates</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Publish a new app version. The newest published release is what every user's app checks when they open it.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchUpdates} disabled={loading} className="au-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 disabled:opacity-60">
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button type="button" onClick={openNew} className="au-press inline-flex items-center gap-2 h-9 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-sm">
            <FaPlus size={11} /> New Release
          </button>
        </div>
      </div>

      {loadError && (
        <div className="au-in mb-4 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 dark:bg-rose-900/20 dark:border-rose-900 p-4 text-sm text-rose-900 dark:text-rose-200">
          <FaExclamationTriangle className="mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">Could not load releases</p>
            <p className="text-xs mt-0.5">{loadError}</p>
          </div>
          <button type="button" onClick={fetchUpdates} className="au-press h-8 px-3 rounded-lg bg-white text-rose-700 text-xs font-semibold border border-rose-200">
            Try again
          </button>
        </div>
      )}

      {/* Live release hero */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="au-in relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-700 to-slate-900 text-white p-6 xl:col-span-2">
          <div className="pointer-events-none absolute -right-16 -top-16 w-72 h-72 rounded-full bg-fuchsia-400/20 blur-3xl" />
          <div className="relative flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="inline-flex items-center gap-2 text-sm text-indigo-100">
                <span className={`w-2.5 h-2.5 rounded-full ${live ? "bg-emerald-400 au-live" : "bg-slate-400"}`} /> Live for users now
              </p>
              {live ? (
                <>
                  <p className="mt-1 text-5xl font-black tracking-tight">v{live.latest_version_name}</p>
                  <p className="text-sm text-indigo-100/80 tabular-nums">
                    Version code {live.latest_version_code} • published {ago(parseAny(live.date_time))}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${isForce(live) ? "bg-rose-500 text-white" : "bg-white/15 text-white"}`}>
                      {isForce(live) ? <FaLock size={9} /> : <FaUnlock size={9} />} {isForce(live) ? "Force update: users must update" : "Optional: users can skip"}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-white/15">
                      <FaDownload size={9} /> {linkKind(live.apk_url)}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <p className="mt-1 text-3xl font-black">No release published</p>
                  <p className="text-sm text-indigo-100/80 mt-1">The app will not ask anyone to update. Publish your first release to start.</p>
                </>
              )}
            </div>
            <div className="flex flex-col gap-2">
              {live && (
                <>
                  <button type="button" onClick={() => setConfirm({ type: "toggleForce", item: live })} className="au-press inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white/15 hover:bg-white/25 text-sm font-semibold">
                    {isForce(live) ? <FaUnlock size={11} /> : <FaLock size={11} />} {isForce(live) ? "Make optional" : "Make it forced"}
                  </button>
                  <a href={live.apk_url} target="_blank" rel="noreferrer" className="au-press inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white/15 hover:bg-white/25 text-sm font-semibold">
                    <FaExternalLinkAlt size={11} /> Test download
                  </a>
                </>
              )}
              <button type="button" onClick={openNew} className="au-press inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white text-indigo-700 text-sm font-bold">
                <FaRocket size={11} /> Publish new version
              </button>
            </div>
          </div>
          {liveBehind && (
            <p className="relative mt-4 flex items-start gap-2 rounded-xl bg-amber-400/20 border border-amber-300/40 p-3 text-xs text-amber-50">
              <FaExclamationTriangle className="mt-0.5 shrink-0" /> v{highest.latest_version_name} (code {highest.latest_version_code}) is higher but is not live, because an older version was published after it. Use "Make live again" on v{highest.latest_version_name} below.
            </p>
          )}
          <div className="relative mt-5 grid grid-cols-3 gap-3 text-center">
            {[
              { label: "Releases", value: stats.total },
              { label: "Forced releases", value: stats.force },
              { label: "Highest code", value: highest ? highest.latest_version_code : "-" },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl bg-white/10 py-3">
                <p className="text-2xl font-black tabular-nums">{loading && !updates.length ? "…" : s.value}</p>
                <p className="text-[11px] text-indigo-100/80">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className={`${CARD} au-in p-5 flex flex-col items-center justify-center`} style={{ animationDelay: "80ms" }}>
          <p className="text-xs font-semibold text-slate-500 mb-3 inline-flex items-center gap-1.5">
            <FaMobileAlt /> What users see on older versions
          </p>
          <Phone name={live?.latest_version_name} force={live && isForce(live)} empty={!live} />
        </div>
      </div>

      {/* Timeline */}
      <div className={`${CARD} au-in mt-4 p-5`} style={{ animationDelay: "160ms" }}>
        <div className="flex items-center justify-between gap-2 mb-4">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white inline-flex items-center gap-2">
            <FaHistory className="text-indigo-500" /> Release history
          </h2>
          <span className="text-xs text-slate-500">Newest first</span>
        </div>

        {loading && !updates.length ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-20 rounded-xl bg-slate-100 dark:bg-slate-700 animate-pulse" />
            ))}
          </div>
        ) : !timeline.length ? (
          <div className="py-12 text-center">
            <FaInbox className="mx-auto text-slate-300" size={36} />
            <p className="mt-3 font-semibold text-slate-700 dark:text-slate-200">No releases yet</p>
            <p className="text-sm text-slate-500">Build the app, upload the APK somewhere, then publish it here.</p>
            <button type="button" onClick={openNew} className="au-press mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-indigo-600 text-white text-sm font-semibold">
              <FaPlus size={11} /> Publish first release
            </button>
          </div>
        ) : (
          <ol className="relative border-l-2 border-slate-100 dark:border-slate-700 ml-3 space-y-4">
            {timeline.map((u, i) => {
              const isLive = live && u.update_id === live.update_id;
              const d = parseAny(u.date_time);
              return (
                <li key={u.update_id} className="au-in ml-6" style={{ animationDelay: `${Math.min(i, 10) * 40}ms` }}>
                  <span className={`absolute -left-[11px] mt-4 w-5 h-5 rounded-full ring-4 ring-white dark:ring-slate-800 flex items-center justify-center ${isLive ? "bg-emerald-500 au-live" : "bg-slate-300 dark:bg-slate-600"}`}>
                    {isLive && <FaBroadcastTower size={8} className="text-white" />}
                  </span>
                  <div className={`rounded-2xl border p-4 ${isLive ? "border-emerald-200 bg-emerald-50/50 dark:bg-emerald-900/10 dark:border-emerald-900" : "border-slate-200 dark:border-slate-700"}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-lg font-bold text-slate-900 dark:text-white">v{u.latest_version_name}</p>
                          <span className="text-xs font-mono text-slate-500">code {u.latest_version_code}</span>
                          {isLive && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white">LIVE</span>}
                          <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${isForce(u) ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"}`}>
                            {isForce(u) ? <FaLock size={8} /> : <FaUnlock size={8} />} {isForce(u) ? "Forced" : "Optional"}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {fmtDate(d)}, {fmtTime(d)} • {ago(d)}
                        </p>
                        <div className="mt-2 flex items-center gap-2 min-w-0">
                          <FaLink size={10} className="text-slate-400 shrink-0" />
                          <a href={u.apk_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline truncate max-w-[420px]" title={u.apk_url}>
                            {fileName(u.apk_url)}
                          </a>
                          <span className="text-[10px] text-slate-400 shrink-0">{linkKind(u.apk_url)}</span>
                          <button type="button" onClick={() => copyText(u.apk_url, "Link copied")} className="text-slate-400 hover:text-slate-700 shrink-0" aria-label="Copy link">
                            <FaCopy size={10} />
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {!isLive && (
                          <button type="button" onClick={() => setConfirm({ type: "republish", item: u })} className="au-press inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-600 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:border-emerald-400 hover:text-emerald-700">
                            <FaRedo size={9} /> Make live again
                          </button>
                        )}
                        <a href={u.apk_url} target="_blank" rel="noreferrer" className="au-press w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300" aria-label="Test download">
                          <FaDownload size={10} />
                        </a>
                        <button type="button" onClick={() => setConfirm({ type: "delete", item: u })} className="au-press w-8 h-8 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white flex items-center justify-center" aria-label="Delete release">
                          <FaTrash size={10} />
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {/* ================= Editor ================= */}
      {editor && (
        <div className="fixed inset-0 flex justify-end" style={{ zIndex: 1100 }}>
          <div className="au-fade absolute inset-0 bg-slate-900/40" onClick={() => !saving && setEditor(false)} />
          <aside className="au-slide relative w-full max-w-3xl h-full bg-white dark:bg-secondary-dark-bg shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <div>
                <p className="text-xs text-slate-500">New release</p>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Publish an app update</h2>
              </div>
              <button type="button" onClick={() => !saving && setEditor(false)} className="au-press w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700" aria-label="Close">
                <FaTimes />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6 p-5">
                <div className="md:col-span-3 space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="au-code" className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                        Version code <span className="text-rose-500">*</span>
                      </label>
                      <input
                        id="au-code"
                        inputMode="numeric"
                        value={form.latest_version_code}
                        onChange={(e) => setForm((f) => ({ ...f, latest_version_code: e.target.value.replace(/\D/g, "") }))}
                        onBlur={() => setTouched((t) => ({ ...t, latest_version_code: 1 }))}
                        placeholder="e.g. 12"
                        className={`${INPUT} h-11 px-3 font-mono ${touched.latest_version_code && errors.latest_version_code ? "border-rose-300" : ""}`}
                      />
                      {touched.latest_version_code && errors.latest_version_code ? (
                        <p className="text-xs text-rose-600 mt-1">{errors.latest_version_code}</p>
                      ) : (
                        <p className="text-xs text-slate-400 mt-1">versionCode from build.gradle{maxCode ? `. Last was ${maxCode}` : ""}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="au-name" className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                        Version name <span className="text-rose-500">*</span>
                      </label>
                      <input
                        id="au-name"
                        value={form.latest_version_name}
                        onChange={(e) => setForm((f) => ({ ...f, latest_version_name: e.target.value }))}
                        onBlur={() => setTouched((t) => ({ ...t, latest_version_name: 1 }))}
                        placeholder="e.g. 2.5.0"
                        className={`${INPUT} h-11 px-3 ${touched.latest_version_name && errors.latest_version_name ? "border-rose-300" : ""}`}
                      />
                      {touched.latest_version_name && errors.latest_version_name ? <p className="text-xs text-rose-600 mt-1">{errors.latest_version_name}</p> : <p className="text-xs text-slate-400 mt-1">What users see, e.g. 2.5.0</p>}
                    </div>
                  </div>
                  {lowerThanLive && (
                    <p className="flex items-start gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 text-xs p-3">
                      <FaExclamationTriangle className="mt-0.5 shrink-0" /> Version code {form.latest_version_code} is not higher than {maxCode}. Phones that already have {maxCode} will not update, and this older version becomes the live one.
                    </p>
                  )}

                  <div>
                    <label htmlFor="au-url" className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                      Download link <span className="text-rose-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        id="au-url"
                        value={form.apk_url}
                        onChange={(e) => setForm((f) => ({ ...f, apk_url: e.target.value }))}
                        onBlur={() => setTouched((t) => ({ ...t, apk_url: 1 }))}
                        placeholder="https://ycnp.in/app/ycnp-v2.5.0.apk"
                        className={`${INPUT} h-11 px-3 flex-1 ${touched.apk_url && errors.apk_url ? "border-rose-300" : ""}`}
                      />
                      {form.apk_url && !errors.apk_url && (
                        <a href={form.apk_url} target="_blank" rel="noreferrer" className="au-press h-11 px-3 rounded-xl border border-slate-200 dark:border-slate-600 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200">
                          <FaExternalLinkAlt size={10} /> Test
                        </a>
                      )}
                    </div>
                    {touched.apk_url && errors.apk_url ? (
                      <p className="text-xs text-rose-600 mt-1">{errors.apk_url}</p>
                    ) : (
                      <p className="text-xs text-slate-400 mt-1">{form.apk_url ? `Detected: ${linkKind(form.apk_url)}. Click Test to make sure it downloads.` : "Direct APK link or Play Store link"}</p>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">Update type</p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { v: 0, title: "Optional", desc: "Users see the prompt but can tap Later", icon: FaUnlock, on: "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20" },
                        { v: 1, title: "Forced", desc: "Users must update before using the app", icon: FaLock, on: "border-rose-500 bg-rose-50 dark:bg-rose-900/20" },
                      ].map((o) => {
                        const Icon = o.icon;
                        const sel = num(form.force_update) === o.v;
                        return (
                          <button key={o.v} type="button" onClick={() => setForm((f) => ({ ...f, force_update: o.v }))} className={`au-press text-left rounded-xl border-2 p-3 ${sel ? o.on : "border-slate-200 dark:border-slate-600"}`}>
                            <p className="text-sm font-semibold text-slate-900 dark:text-white inline-flex items-center gap-2">
                              <Icon size={11} /> {o.title} {sel && <FaCheck size={10} className="text-emerald-600" />}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">{o.desc}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <p className="flex items-start gap-2 rounded-xl bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 text-xs p-3">
                    <FaInfoCircle className="mt-0.5 shrink-0 text-indigo-500" /> As soon as you publish, every user whose app has a lower version code gets the update prompt the next time they unlock the app.
                  </p>
                </div>

                <div className="md:col-span-2">
                  <p className="text-xs font-semibold text-slate-500 mb-3 text-center">Preview</p>
                  <Phone name={form.latest_version_name} force={num(form.force_update) === 1} />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <span className="hidden sm:inline text-xs text-slate-500">Ctrl + Enter to publish</span>
              <div className="flex gap-2 ml-auto">
                <button type="button" onClick={() => setEditor(false)} disabled={saving} className="au-press h-10 px-4 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200">
                  Cancel
                </button>
                <button type="button" onClick={save} disabled={saving} className="au-press inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-60">
                  {saving ? <FaSyncAlt className="animate-spin" size={12} /> : <FaRocket size={11} />}
                  {saving ? "Publishing..." : "Publish release"}
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* ================= Confirm ================= */}
      {confirm && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="au-fade absolute inset-0 bg-slate-900/60" onClick={() => !working && setConfirm(null)} />
          <div className="au-zoom relative w-full max-w-md rounded-2xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            {(() => {
              const { type, item } = confirm;
              const isLive = live && item.update_id === live.update_id;
              const prev = timeline.find((u) => u.update_id !== item.update_id);
              const copy =
                type === "delete"
                  ? {
                      title: `Delete v${item.latest_version_name}?`,
                      body: isLive
                        ? prev
                          ? `This is the live release. After deleting, v${prev.latest_version_name} becomes live again.`
                          : "This is the only release. After deleting, the app stops asking users to update."
                        : "This old release is removed from the history.",
                      btn: "Yes, delete",
                      cls: "bg-rose-600 hover:bg-rose-700",
                      icon: <FaTrash size={16} />,
                      tint: "bg-rose-100 text-rose-600",
                    }
                  : type === "toggleForce"
                  ? {
                      title: isForce(item) ? "Make the update optional?" : "Force everyone to update?",
                      body: isForce(item) ? "Users will be able to tap Later and keep using their current version." : "Users on older versions will not be able to use the app until they install this update.",
                      btn: isForce(item) ? "Yes, make optional" : "Yes, force update",
                      cls: isForce(item) ? "bg-indigo-600 hover:bg-indigo-700" : "bg-rose-600 hover:bg-rose-700",
                      icon: isForce(item) ? <FaUnlock size={16} /> : <FaLock size={16} />,
                      tint: "bg-indigo-100 text-indigo-600",
                    }
                  : {
                      title: `Make v${item.latest_version_name} live again?`,
                      body: "It is published again with today's date, so the app starts offering this version.",
                      btn: "Yes, make live",
                      cls: "bg-emerald-600 hover:bg-emerald-700",
                      icon: <FaRedo size={16} />,
                      tint: "bg-emerald-100 text-emerald-600",
                    };
              return (
                <>
                  <span className={`w-12 h-12 rounded-2xl flex items-center justify-center ${copy.tint}`}>{copy.icon}</span>
                  <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">{copy.title}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{copy.body}</p>
                  {type === "toggleForce" && <p className="mt-3 text-xs text-slate-500">This publishes the same version again with the new setting.</p>}
                  <div className="flex gap-2 mt-5">
                    <button type="button" onClick={() => setConfirm(null)} disabled={working} className="au-press flex-1 h-11 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                      Go back
                    </button>
                    <button type="button" onClick={runConfirm} disabled={working} className={`au-press flex-1 h-11 rounded-xl text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 ${copy.cls}`}>
                      {working && <FaSyncAlt className="animate-spin" size={12} />}
                      {working ? "Please wait..." : copy.btn}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppUpdates;